/**
 * v2 API contract of «Обзор» (/overview, decisions-log V157, V158, V300): every project of the month
 * with «Рассчитано» — planned while the invoice is not issued, the invoice's lines of the month once
 * it is — «Получено» (what landed for those lines, net of bank fees), its costs as «ФОП»,
 * «Налоги ФОП», «Налог с выручки» and «Прочие» (V159), margin and margin %, in USD, and the totals.
 */
import { z } from 'zod';
import { isPeriod } from '../dates';
import type { FxEstimate } from './fxEstimate';

export const overviewQuerySchema = z.object({
  month: z.string().refine(isPeriod, 'Месяц в формате ГГГГ-ММ'),
  issuer: z.string().min(1).default('all'),
});
export type OverviewQuery = z.infer<typeof overviewQuerySchema>;

/**
 * Where «Рассчитано» comes from: `plan` — the month's amount from «План проектов», no invoice yet;
 * `invoiced` — the issued invoice, not paid in full; `paid` — paid in full; `none` — nothing to bill.
 */
export const OVERVIEW_REVENUE_STATES = ['plan', 'invoiced', 'paid', 'none'] as const;
export type OverviewRevenueState = (typeof OVERVIEW_REVENUE_STATES)[number];

export interface OverviewFigures {
  /** «Рассчитано»: planned or invoiced, in USD. */
  readonly revenueMinor: number;
  /** «Получено» (V300): paid of the invoice, the month's share of the project's lines, net of bank fees, in USD. */
  readonly receivedMinor: number;
  /** «ФОП»: the pay of the team — payouts and the salary through our entities on hand. */
  readonly payrollMinor: number;
  /** «Налоги ФОП» (V159): the taxes on the pay — recorded payments, else computed from the entity's rates. */
  readonly payrollTaxMinor: number;
  /** «Налог с выручки». */
  readonly revenueTaxMinor: number;
  /** «Прочие»: the project's expenses of «Расходы» dated in the month (V312). */
  readonly otherMinor: number;
  /** ФОП + налоги ФОП + налог с выручки + прочие. */
  readonly costMinor: number;
  /** «Рассчитано» less the costs. */
  readonly marginMinor: number;
  /** margin / revenue, 2 decimals; null without revenue. */
  readonly marginPct: number | null;
  /** Amounts left out for a missing FX rate. */
  readonly unconverted: number;
  /** The figures converted at a rate that is not final yet (V197); margin and the sum of costs follow them. */
  readonly estimates: OverviewEstimates;
}

export interface OverviewEstimates {
  readonly revenue: FxEstimate | null;
  readonly received: FxEstimate | null;
  readonly payroll: FxEstimate | null;
  readonly payrollTax: FxEstimate | null;
  readonly revenueTax: FxEstimate | null;
  readonly other: FxEstimate | null;
}

export interface OverviewRow extends OverviewFigures {
  /** null: costs not tied to a project. */
  readonly projectId: string | null;
  readonly projectName: string;
  readonly clientName: string | null;
  readonly revenueState: OverviewRevenueState;
}

export interface OverviewResponse {
  readonly period: string;
  readonly currency: string;
  readonly rows: readonly OverviewRow[];
  readonly totals: OverviewFigures;
}
